import React, { FC, PropsWithChildren, useRef } from 'react';
import { Formik, FormikProps } from 'formik';
import { BodyText, BodyTextLarge, BodyTextSmall, FlexCol, FlexRow, H2Text, H4Text } from '@minecraft.atoms';
import Icon from '@minecraft.icon';
import { MeatballDropdownButton, MeatballDropdownButtonProps } from '@minecraft.molecules';
import { useTranslation } from '@blocs.i18n';
import { CARD_TYPES, PAYMENT_METHOD_TYPE, PAYMENT_TYPES } from '../../enums/PaymentMethods';
import { APPLE_PAY, PAYPAL } from '../../constants';
import { displayCardIcon, displayCardType, isPaymentTypeApplePay } from '../../CreditCard/CreditCard';
import { UseBilling } from '../../useBilling';
import { CheckoutProps } from '../types';
import { PaymentPriorityCheckboxes } from './PaymentPriorityCheckboxes';

type CreditCardPaymentMethodSummaryProps = {
  onEditClick: () => void;
  selectedCard: UseBilling['wallet'][0];
  countryName?: string;
  toggleRemovePaymentModal?: () => void;
  disabled?: boolean;
};

const PayPalPaymentMethodSummary: FC = () => {
  return (
    <FlexRow alignItems="center" gap="3">
      <Icon name="paypal" /> <BodyTextLarge fontWeight="semibold">{PAYPAL}</BodyTextLarge>
    </FlexRow>
  );
};

const ApplePayPaymentMethodSummary: FC<{ selectedCard: UseBilling['wallet'][0] }> = ({ selectedCard }) => {
  const { t } = useTranslation();
  return (
    <FlexCol gap="1">
      <FlexRow w="full" gap="6" alignItems="center">
        <Icon height={3.4} name={CARD_TYPES.ApplePay} />
        <H4Text fontWeight="semibold">{APPLE_PAY}</H4Text>
      </FlexRow>
      <FlexRow w="full" gap="6" alignItems="center">
        <BodyTextSmall>
          {t('talent:billing.membership.expiresOn', {
            date: `${selectedCard.month}/${selectedCard.year}`,
          })}
        </BodyTextSmall>
      </FlexRow>
    </FlexCol>
  );
};

type BillingAddressSectionProps = {
  selectedCard: UseBilling['wallet'][0];
  countryName?: string;
};

// Only shown when the saved payment method actually has an address on file — Apple Pay / PayPal
// entries commonly don't.
const BillingAddressSection: FC<BillingAddressSectionProps> = ({ selectedCard, countryName }) => {
  const { t } = useTranslation();
  const hasBillingAddress = Boolean(
    selectedCard?.street1 ||
      selectedCard?.street2 ||
      selectedCard?.city ||
      selectedCard?.region ||
      selectedCard?.postalCode
  );

  if (!hasBillingAddress) return null;

  return (
    <>
      <H2Text>{t('common:paywall.checkout.billingAddress.title')}</H2Text>
      <FlexCol gap="2">
        <BodyText>{selectedCard?.street1}</BodyText>
        {selectedCard?.street2 && <BodyText>{selectedCard?.street2}</BodyText>}
        <BodyText>
          {selectedCard?.city ? `${selectedCard?.city}, ` : ''} {selectedCard?.region} {selectedCard?.postalCode}
        </BodyText>
        <BodyText>{countryName}</BodyText>
      </FlexCol>
    </>
  );
};

const CreditCardPaymentMethodSummary: FC<CreditCardPaymentMethodSummaryProps> = ({
  selectedCard,
  onEditClick,
  countryName,
  toggleRemovePaymentModal,
  disabled,
}) => {
  const { t } = useTranslation();
  const cardIcon = displayCardIcon(selectedCard?.cardType, selectedCard?.paymentType);

  const menuItemsProps: MeatballDropdownButtonProps['menuItemsProps'] = [
    { id: 'edit-payment-method', label: t('common:button.edit'), onClick: onEditClick },
    ...(selectedCard?.isPrimaryPaymentMethod
      ? []
      : [{ id: 'delete-payment-method', label: t('common:button.delete'), onClick: toggleRemovePaymentModal }]),
  ];

  return (
    <FlexCol
      data-testid="payment-method-summary"
      w="full"
      bgColor="card"
      borderPlacement="all"
      padding="4"
      gap="6"
      borderRadius="sm"
      mdProps={{ w: '2/3' }}
    >
      <FlexRow justifyContent="space-between" alignItems="center" w="full">
        <H2Text>{t('common:billing.card.paymentMethod')}</H2Text>
        <MeatballDropdownButton
          data-testid="payment-method-summary-menu"
          toggleProps={{ label: t('common:label.openMenu'), disabled }}
          menuItemsProps={menuItemsProps}
          menuContainer="body"
        />
      </FlexRow>
      <FlexRow alignItems="center" gap="3">
        {cardIcon && <Icon height={3.4} name={cardIcon} />}
        <FlexCol>
          <BodyTextLarge fontWeight="medium">
            {displayCardType(selectedCard?.cardType, selectedCard?.paymentType)}
          </BodyTextLarge>
          {selectedCard?.lastFour && (
            <BodyTextSmall noWrap>{t('common:billing.card.name', { lastFour: selectedCard?.lastFour })}</BodyTextSmall>
          )}
        </FlexCol>
      </FlexRow>
      <BillingAddressSection selectedCard={selectedCard} countryName={countryName} />
    </FlexCol>
  );
};

type SavedPaymentMethodEditorProps = {
  selectedCard: UseBilling['wallet'][0];
  handlePaymentPriorityChange?: CheckoutProps['handlePaymentPriorityChange'];
  toggleRemovePaymentModal?: () => void;
  disabled?: boolean;
  countryName?: string;
};

// PayPal / Apple Pay wallet entries have no editable fields — "editing" one only means changing
// its primary/backup priority or deleting it, so this chrome (title, priority checkboxes, delete
// menu item) is shown directly rather than gated behind a separate Edit step.
const SavedPaymentMethodEditor: FC<PropsWithChildren<SavedPaymentMethodEditorProps>> = ({
  selectedCard,
  handlePaymentPriorityChange,
  toggleRemovePaymentModal,
  disabled,
  countryName,
  children,
}) => {
  const { t } = useTranslation();
  const formikRef = useRef<FormikProps<{ paymentPriority: PAYMENT_METHOD_TYPE[] }>>(null);
  const paymentPriority: PAYMENT_METHOD_TYPE[] = [];
  if (selectedCard?.isPrimaryPaymentMethod) paymentPriority.push(PAYMENT_METHOD_TYPE.PRIMARY);
  if (selectedCard?.isBackupPaymentMethod) paymentPriority.push(PAYMENT_METHOD_TYPE.BACKUP);

  const handleTogglePriority = (type: PAYMENT_METHOD_TYPE.PRIMARY | PAYMENT_METHOD_TYPE.BACKUP, isChecked: boolean) => {
    const currentPriority = formikRef.current?.values?.paymentPriority ?? [];
    const newPriority = isChecked
      ? [...currentPriority, type]
      : currentPriority.filter((priority) => priority !== type);
    formikRef.current?.setFieldValue('paymentPriority', newPriority);

    handlePaymentPriorityChange?.({
      billingInfoId: selectedCard?.id?.toString(),
      isPrimaryPaymentMethod: type === PAYMENT_METHOD_TYPE.PRIMARY ? isChecked : undefined,
      isBackupPaymentMethod: type === PAYMENT_METHOD_TYPE.BACKUP ? isChecked : undefined,
    });
  };

  // Apple Pay / PayPal entries never have an Edit option — the priority checkboxes below are
  // already the full extent of what's editable — so this menu only ever offers Delete, and only
  // when this isn't the current primary payment method.
  const menuItemsProps: MeatballDropdownButtonProps['menuItemsProps'] = selectedCard?.isPrimaryPaymentMethod
    ? []
    : [{ id: 'delete-payment-method', label: t('common:button.delete'), onClick: toggleRemovePaymentModal }];

  return (
    <FlexCol
      data-testid="add-payment-container"
      w="full"
      bgColor="card"
      borderPlacement="all"
      padding="4"
      gap="6"
      borderRadius="sm"
      mdProps={{ w: '2/3' }}
    >
      <FlexRow justifyContent="space-between" alignItems="center" w="full">
        <H2Text>{t('common:billing.card.edit')}</H2Text>
        {menuItemsProps.length > 0 && (
          <MeatballDropdownButton
            data-testid="payment-method-summary-menu"
            toggleProps={{ label: t('common:label.openMenu'), disabled }}
            menuItemsProps={menuItemsProps}
            menuContainer="body"
          />
        )}
      </FlexRow>
      <Formik
        key={`${selectedCard?.id}-${paymentPriority.join()}`}
        innerRef={formikRef}
        initialValues={{ paymentPriority }}
        onSubmit={() => undefined}
      >
        <PaymentPriorityCheckboxes onToggle={handleTogglePriority} />
      </Formik>
      {children}
      <BillingAddressSection selectedCard={selectedCard} countryName={countryName} />
    </FlexCol>
  );
};

export type SavedPaymentMethodSummaryProps = {
  selectedCard: UseBilling['wallet'][0];
  onEditClick: () => void;
  handlePaymentPriorityChange?: CheckoutProps['handlePaymentPriorityChange'];
  toggleRemovePaymentModal?: () => void;
  disabled?: boolean;
  countryName?: string;
};

export const SavedPaymentMethodSummary: FC<SavedPaymentMethodSummaryProps> = ({
  selectedCard,
  onEditClick,
  handlePaymentPriorityChange,
  toggleRemovePaymentModal,
  disabled,
  countryName,
}) => {
  const isApplePaySelected = isPaymentTypeApplePay(selectedCard?.paymentType);
  const isPayPalSelected = selectedCard?.paymentType === PAYMENT_TYPES.PAYPAL;

  if (isApplePaySelected || isPayPalSelected) {
    return (
      <SavedPaymentMethodEditor
        selectedCard={selectedCard}
        handlePaymentPriorityChange={handlePaymentPriorityChange}
        toggleRemovePaymentModal={toggleRemovePaymentModal}
        disabled={disabled}
        countryName={countryName}
      >
        {isApplePaySelected && <ApplePayPaymentMethodSummary selectedCard={selectedCard} />}
        {isPayPalSelected && <PayPalPaymentMethodSummary />}
      </SavedPaymentMethodEditor>
    );
  }

  return (
    <CreditCardPaymentMethodSummary
      selectedCard={selectedCard}
      onEditClick={onEditClick}
      countryName={countryName}
      toggleRemovePaymentModal={toggleRemovePaymentModal}
      disabled={disabled}
    />
  );
};
